/**
 * Hex grid math. The board is a rectangle of POINTY-TOP hexes in "odd-r" offset layout
 * (odd rows are shifted half a hex to the right). Every hex is addressed by a single
 * integer `HexId = row * width + col`, which lets all per-hex data live in typed arrays.
 *
 * Direction indices (pointy-top, y grows downwards on screen):
 *   0 = E, 1 = NE, 2 = NW, 3 = W, 4 = SW, 5 = SE      opposite(d) = (d + 3) % 6
 *
 * Corner indices (angle = 60°·i − 30°, y down):
 *   0 = upper-right, 1 = lower-right, 2 = bottom, 3 = lower-left, 4 = upper-left, 5 = top
 * The edge shared with neighbour `d` runs between corners EDGE_CORNERS[d].
 */

export type HexId = number;
export const NO_HEX: HexId = -1;

/** Corner-to-centre radius of a hex in world units (pixels at zoom 1). */
export const HEX_SIZE = 36;
export const SQRT3 = Math.sqrt(3);
/** Horizontal distance between neighbouring hex centres in a row. */
export const HEX_W = SQRT3 * HEX_SIZE;
/** Vertical distance between rows. */
export const HEX_ROW_H = 1.5 * HEX_SIZE;

export const DIR_COUNT = 6;
export const Dir = { E: 0, NE: 1, NW: 2, W: 3, SW: 4, SE: 5 } as const;
export type Dir = (typeof Dir)[keyof typeof Dir];

export const opposite = (d: number): number => (d + 3) % 6;

/** Corner pair [a, b] of the edge facing direction d. */
export const EDGE_CORNERS: ReadonlyArray<readonly [number, number]> = [
  [0, 1],
  [5, 0],
  [4, 5],
  [3, 4],
  [2, 3],
  [1, 2],
];

// Offset deltas [dCol, dRow] per direction for even and odd rows (odd-r layout).
const EVEN_ROW: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [0, -1],
  [-1, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];
const ODD_ROW: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [0, 1],
  [1, 1],
];

/** Axial direction vectors [dq, dr] (same order as Dir). */
export const AXIAL_DIRS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

export interface Point {
  x: number;
  y: number;
}

/**
 * Geometry + topology helper for a board of fixed size. Cheap to construct; the neighbour
 * table is precomputed so hot loops (pathfinding, supply, vision) avoid recomputation.
 */
export class HexGrid {
  readonly width: number;
  readonly height: number;
  readonly count: number;
  /** neighbours[h * 6 + d] = neighbour HexId in direction d, or NO_HEX at the board edge. */
  readonly neighbours: Int32Array;
  /** World-space centre of each hex (x at [2h], y at [2h+1]). */
  readonly centers: Float32Array;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.count = width * height;
    this.neighbours = new Int32Array(this.count * 6).fill(NO_HEX);
    this.centers = new Float32Array(this.count * 2);
    for (let row = 0; row < height; row++) {
      const deltas = row & 1 ? ODD_ROW : EVEN_ROW;
      for (let col = 0; col < width; col++) {
        const h = row * width + col;
        for (let d = 0; d < 6; d++) {
          const nc = col + deltas[d][0];
          const nr = row + deltas[d][1];
          if (nc >= 0 && nc < width && nr >= 0 && nr < height) this.neighbours[h * 6 + d] = nr * width + nc;
        }
        this.centers[h * 2] = HEX_W * (col + 0.5 * (row & 1)) + HEX_W / 2;
        this.centers[h * 2 + 1] = HEX_SIZE * (1.5 * row + 1);
      }
    }
  }

  /** Total world-space size of the board. */
  get worldWidth(): number {
    return HEX_W * (this.width + 0.5);
  }
  get worldHeight(): number {
    return HEX_SIZE * (1.5 * (this.height - 1) + 2);
  }

  id(col: number, row: number): HexId {
    if (col < 0 || col >= this.width || row < 0 || row >= this.height) return NO_HEX;
    return row * this.width + col;
  }
  col(h: HexId): number {
    return h % this.width;
  }
  row(h: HexId): number {
    return (h / this.width) | 0;
  }
  inBounds(h: HexId): boolean {
    return h >= 0 && h < this.count;
  }

  neighbour(h: HexId, d: number): HexId {
    return this.neighbours[h * 6 + d];
  }

  /** Iterate valid neighbours. Callback receives (neighbour, direction). */
  forNeighbours(h: HexId, fn: (n: HexId, d: number) => void): void {
    const base = h * 6;
    for (let d = 0; d < 6; d++) {
      const n = this.neighbours[base + d];
      if (n !== NO_HEX) fn(n, d);
    }
  }

  neighbourList(h: HexId): HexId[] {
    const out: HexId[] = [];
    this.forNeighbours(h, (n) => out.push(n));
    return out;
  }

  /** Direction from a to its neighbour b, or -1 if not adjacent. */
  dirTo(a: HexId, b: HexId): number {
    const base = a * 6;
    for (let d = 0; d < 6; d++) if (this.neighbours[base + d] === b) return d;
    return -1;
  }

  areNeighbours(a: HexId, b: HexId): boolean {
    return this.dirTo(a, b) >= 0;
  }

  // ---- coordinate systems ----

  toAxial(h: HexId): [q: number, r: number] {
    const col = this.col(h);
    const row = this.row(h);
    return [col - (row - (row & 1)) / 2, row];
  }

  fromAxial(q: number, r: number): HexId {
    return this.id(q + (r - (r & 1)) / 2, r);
  }

  distance(a: HexId, b: HexId): number {
    const [aq, ar] = this.toAxial(a);
    const [bq, br] = this.toAxial(b);
    const dq = aq - bq;
    const dr = ar - br;
    return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
  }

  /** All hexes within `radius` steps of `center` (including center). */
  range(center: HexId, radius: number): HexId[] {
    const out: HexId[] = [];
    const [cq, cr] = this.toAxial(center);
    for (let dq = -radius; dq <= radius; dq++) {
      const lo = Math.max(-radius, -dq - radius);
      const hi = Math.min(radius, -dq + radius);
      for (let dr = lo; dr <= hi; dr++) {
        const h = this.fromAxial(cq + dq, cr + dr);
        if (h !== NO_HEX) out.push(h);
      }
    }
    return out;
  }

  /** Hexes exactly `radius` steps away. */
  ring(center: HexId, radius: number): HexId[] {
    if (radius === 0) return [center];
    const out: HexId[] = [];
    const [cq, cr] = this.toAxial(center);
    let q = cq + AXIAL_DIRS[4][0] * radius;
    let r = cr + AXIAL_DIRS[4][1] * radius;
    for (let side = 0; side < 6; side++) {
      for (let step = 0; step < radius; step++) {
        const h = this.fromAxial(q, r);
        if (h !== NO_HEX) out.push(h);
        q += AXIAL_DIRS[side][0];
        r += AXIAL_DIRS[side][1];
      }
    }
    return out;
  }

  /** Hexes on the straight line from a to b (inclusive), cube-lerp based. */
  line(a: HexId, b: HexId): HexId[] {
    const n = this.distance(a, b);
    const [aq, ar] = this.toAxial(a);
    const [bq, br] = this.toAxial(b);
    const out: HexId[] = [];
    for (let i = 0; i <= n; i++) {
      const t = n === 0 ? 0 : i / n;
      // nudge avoids ambiguous rounding exactly on edges
      const q = aq + (bq - aq) * t + 1e-6;
      const r = ar + (br - ar) * t + 1e-6;
      const h = this.roundAxial(q, r);
      if (h !== NO_HEX && out[out.length - 1] !== h) out.push(h);
    }
    return out;
  }

  roundAxial(q: number, r: number): HexId {
    const s = -q - r;
    let rq = Math.round(q);
    let rr = Math.round(r);
    const rs = Math.round(s);
    const dq = Math.abs(rq - q);
    const dr = Math.abs(rr - r);
    const ds = Math.abs(rs - s);
    if (dq > dr && dq > ds) rq = -rr - rs;
    else if (dr > ds) rr = -rq - rs;
    return this.fromAxial(rq, rr);
  }

  // ---- world space ----

  center(h: HexId): Point {
    return { x: this.centers[h * 2], y: this.centers[h * 2 + 1] };
  }
  cx(h: HexId): number {
    return this.centers[h * 2];
  }
  cy(h: HexId): number {
    return this.centers[h * 2 + 1];
  }

  /** World position of corner i (0..5) of hex h. */
  corner(h: HexId, i: number, size = HEX_SIZE): Point {
    const angle = (Math.PI / 180) * (60 * i - 30);
    return { x: this.cx(h) + size * Math.cos(angle), y: this.cy(h) + size * Math.sin(angle) };
  }

  /** Midpoint of the edge facing direction d. */
  edgeMidpoint(h: HexId, d: number): Point {
    const [a, b] = EDGE_CORNERS[d];
    const p = this.corner(h, a);
    const q = this.corner(h, b);
    return { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 };
  }

  /** Hex containing world point (x, y), or NO_HEX if outside the board. */
  fromWorld(x: number, y: number): HexId {
    // Undo the half-hex origin offset, then invert the pointy-top axial projection.
    const px = x - HEX_W / 2;
    const py = y - HEX_SIZE;
    const q = ((SQRT3 / 3) * px - (1 / 3) * py) / HEX_SIZE;
    const r = ((2 / 3) * py) / HEX_SIZE;
    return this.roundAxial(q, r);
  }
}
